"use client";

import {NeoButton, NeoTextBox} from "@/components/forms";
import type {DateValue} from "@internationalized/date";
import {getLocalTimeZone, today} from "@internationalized/date";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RangeCalendar
} from "@nextui-org/react";
import type {RangeValue} from "@react-types/shared";
import React, {useEffect, useState} from "react";
import {FaCalendarAlt} from "react-icons/fa";

const tz = getLocalTimeZone();
const maxDate = today(tz);
//const minDate = today(tz).subtract({ days: 6 });
const pad2 = (n: number) => String(n).padStart(2, "0");

const toMysqlDateTime = (d: any, timeHHmm: string, seconds = "00") => {
  const [hh, mm] = timeHHmm.split(":").map(Number);
  return `${d.year}-${pad2(d.month)}-${pad2(d.day)} ${pad2(hh)}:${pad2(mm)}:${seconds}`;
};

type NeoDateTimePickerProps = {
  handleDateChange?: (range: any) => void;
};

export const NeoDateTimePicker: React.FC<NeoDateTimePickerProps> = ({
  handleDateChange = () => {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [range, setRange] = useState<RangeValue<DateValue> | null>({
    start: maxDate,
    end: maxDate
  });
  const [fromTime, setFromTime] = useState("00:00");
  const [toTime, setToTime] = useState("23:59");
  const [daterange, setDaterange] = useState("");

  const formatDecDateTime = (date: Date) => {
    const datestring = new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      //year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    }).format(date);

    // "Dec 11, 2026, 12:00 AM" -> "DEC 11 2026 12:00AM"
    //return s.replaceAll(",", "").replace(" AM", "AM").replace(" PM", "PM").toUpperCase();
    return datestring;
  };

  useEffect(() => {
    const start = new Date();
    start.setHours(0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59);
    const returndata = {from: start, to: end};
    handleDateChange(returndata);
    const defaultdaterange =
      formatDecDateTime(start) + " - " + formatDecDateTime(end);
    setDaterange(defaultdaterange);
  }, []);

  const onSubmit = () => {
    if (!range?.start || !range?.end) return;

    const from = toMysqlDateTime(range.start, fromTime, "00");
    const to = toMysqlDateTime(range.end, toTime, "59");
    const startDate = new Date(from);
    const endDate = new Date(to);
    const defaultdaterange =
      formatDecDateTime(startDate) + " - " + formatDecDateTime(endDate);
    setDaterange(defaultdaterange);
    const returndata = {from: startDate, to: endDate};
    handleDateChange(returndata);
    setIsOpen(false);
  };

  return (
    <div className="flex w-full max-w-xl flex-row gap-1">
      <div className="flex flex-grow flex-row gap-1">
        <NeoTextBox
          size="sm"
          readOnly
          value={daterange}
          onClick={() => setIsOpen(true)}
        />
      </div>
      <Popover
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        placement="bottom-start"
      >
        <PopoverTrigger>
          <Button
            className="-mt-2"
            isIconOnly
            variant="light"
            onPress={() => setIsOpen(true)}
          >
            <FaCalendarAlt />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0">
          <div className="p-1">
            <RangeCalendar
              maxValue={maxDate}
              visibleMonths={1}
              value={range}
              onChange={val => setRange(val)}
            />
            <div className="grid grid-cols-2 gap-1 p-1">
              <div>
                <NeoTextBox
                  label={"From Time"}
                  type="time"
                  size="sm"
                  value={fromTime}
                  onChange={e => setFromTime(e.target.value)}
                />
              </div>
              <div>
                <NeoTextBox
                  label={"To Time"}
                  type="time"
                  size="sm"
                  value={toTime}
                  onChange={e => setToTime(e.target.value)}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t px-3 py-2">
              <NeoButton
                size="sm"
                variant="light"
                color="white"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </NeoButton>
              <NeoButton size="sm" onClick={onSubmit}>
                Apply
              </NeoButton>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
